import { JobPhase, Role } from '@prisma/client';
import { PermissionKey } from '@schema/permission.schema';
import type { CustomJobPhase } from '@services/context.service';
import { type JobNavItemId } from '~/layout/jobNavItems';
import { useJobContext } from '~/react_context/JobContext';
import { useUserContext } from '~/react_context/UserContext';
import { JobSubView } from '~/server/schema/job.schema';

// Re-exported for existing consumers; the canonical list lives in ~/layout/jobNavItems.
export { JOB_NAV_ITEM_OPTIONS } from '~/layout/jobNavItems';

// Each item's `id` is typed as a `JobNavItemId`; the `id` literals must come from the canonical list.
type JobNavItem = {
	allowedRoles: Role[];
	compareHref?: string;
	href: string;
	icon: string;
	id: JobNavItemId;
	isNew?: boolean;
	name: string;
	permissionKey: PermissionKey;
};

/**
 * Compile-time assertion that the ids built by `useJobNavItemsForRole` exactly match the canonical
 * `JobNavItemId` union from `~/layout/jobNavItems`. If a nav item is added/removed in only one place,
 * `Equals` resolves to `false` and the `true` constraint fails the build, forcing the lists to stay
 * in sync. `T` is the union of ids the hook actually builds (see `assertJobNavIdsAreExhaustive`).
 */
type Equals<A, B> = (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false;
const assertJobNavIdsAreExhaustive = <T extends JobNavItemId>(_built: Equals<T, JobNavItemId> extends true ? true : never) => {};

// Function to check if the user has a specific permission
function hasPermission(userPermissions: PermissionKey[], permission: PermissionKey): boolean {
	return userPermissions.includes(permission);
}

// Function to check if the user's role is allowed for a particular item
function isRoleAllowed(role: Role, allowedRoles: Role[]): boolean {
	return allowedRoles.includes(role); // Ensure role is not null
}

const estimatesNavAccess = { allowedRoles: [Role.FULL], permissionKey: PermissionKey.JOB_MENU_ESTIMATES };

export const useCanOpenJobEstimates = () => {
	const { role, allowedPermissions } = useUserContext();
	const { hiddenJobNavItemIds } = useJobContext();
	return (
		role != null &&
		hasPermission(allowedPermissions, estimatesNavAccess.permissionKey) &&
		isRoleAllowed(role, estimatesNavAccess.allowedRoles) &&
		!hiddenJobNavItemIds.includes('job-nav-estimates')
	);
};

export const useSideNavItemsForRole = (role: Role, userPermissions: PermissionKey[]) => {
	const {
		unreadNotificationsCount,
		taskCountWithEmailDeliveryFailed,
		businessCategorySummary,
		quickEstimatesCount,
		enable2WayEmail,
		enableReachAiVoiceDemo,
		enableReachMarketingDemo,
		enableRevenueAgent,
		isPlaybooksFeatureEnabled,
		isSuperAdmin,
		openLeadsCount,
	} = useUserContext();

	const accountingSubItems = [
		{
			name: 'Billing',
			href: '/billing',
			icon: 'file-invoice-dollar',
			permissionKey: PermissionKey.MENU_TRANSACTIONS,
			allowedRoles: [Role.FULL],
		},
		{
			name: 'Transactions',
			href: '/transactions',
			icon: 'money-check-dollar',
			permissionKey: PermissionKey.MENU_TRANSACTIONS,
			allowedRoles: [Role.FULL],
		},
		{
			name: 'Aging Report',
			href: '/aging-report',
			icon: 'money-check-edit-alt',
			permissionKey: PermissionKey.MENU_TRANSACTIONS,
			allowedRoles: [Role.FULL],
		},
		{
			name: 'AP Invoices',
			href: '/ap-invoices',
			icon: 'file-invoice',
			permissionKey: PermissionKey.MENU_TRANSACTIONS,
			allowedRoles: [Role.FULL],
		},
		{
			name: 'AIA Billing',
			href: '/aia-billing',
			icon: 'file-contract',
			permissionKey: PermissionKey.MENU_TRANSACTIONS,
			allowedRoles: [Role.FULL],
		},
		{
			name: 'Batch Syncing',
			href: '/billing/accounting-batch',
			icon: 'sync',
			permissionKey: PermissionKey.MENU_TRANSACTIONS,
			allowedRoles: [Role.FULL],
		},
		{
			name: 'Payroll Timesheets',
			icon: 'clock',
			href: '/reporting/timesheets',
			// Deliberately MENU_TIME_TRACKING rather than MENU_TRANSACTIONS: this lists payroll
			// hours, and whoever can see them on /time-tracking should see where they went.
			permissionKey: PermissionKey.MENU_TIME_TRACKING,
			allowedRoles: [Role.FULL],
		},
	].filter((item) => hasPermission(userPermissions, item.permissionKey) && isRoleAllowed(role, item.allowedRoles));

	function sumNotificationByCategory(key: CustomJobPhase) {
		return businessCategorySummary === undefined
			? 0
			: Object.entries(businessCategorySummary[key]).reduce((acc, [_, value]) => acc + value.jobCount, 0);
	}

	const navItems = {
		navigation: [
			{
				name: 'Dashboard',
				icon: 'gauge-circle-bolt',
				href: '/',
				permissionKey: PermissionKey.MENU_DASHBOARD,
				allowedRoles: [Role.FULL, Role.TECHNICIAN],
			},
			businessCategorySummary?.[JobPhase.ESTIMATE]
				? {
						name: 'Bids',
						icon: 'bullseye-arrow',
						href: `/jobs?subView=${JobSubView.ALL_OPPORTUNITIES}`,
						notification: sumNotificationByCategory(JobPhase.ESTIMATE),
						permissionKey: PermissionKey.MENU_JOBS,
						allowedRoles: [Role.FULL],
					}
				: {
						name: 'Jobs',
						icon: 'bullseye-arrow',
						href: '/jobs',
						permissionKey: PermissionKey.MENU_JOBS,
						allowedRoles: [Role.FULL, Role.TECHNICIAN],
					},
			businessCategorySummary?.[JobPhase.WORK_ORDER]
				? {
						name: 'Won Jobs',
						icon: 'briefcase',
						href: `/jobs?subView=${JobSubView.ALL_WON_JOBS}`,
						notification: sumNotificationByCategory(JobPhase.WORK_ORDER),
						permissionKey: PermissionKey.MENU_JOBS,
						allowedRoles: [Role.FULL],
					}
				: {
						name: 'Won Jobs',
						icon: 'briefcase',
						href: `/jobs?subView=${JobSubView.ALL_WON_JOBS}`,
						permissionKey: PermissionKey.MENU_JOBS,
						allowedRoles: [Role.FULL],
					},
			{
				name: 'Closed',
				icon: 'lock',
				href: `/jobs?subView=${JobSubView.CLOSED}`,
				permissionKey: PermissionKey.MENU_JOBS,
				allowedRoles: [Role.FULL],
			},
			businessCategorySummary?.['FLAGGED']
				? {
						name: 'Flagged',
						icon: 'flag-checkered',
						href: `/jobs?subView=${JobSubView.FLAGGED}`,
						notification: sumNotificationByCategory('FLAGGED'),
						permissionKey: PermissionKey.MENU_JOBS,
						allowedRoles: [Role.FULL],
					}
				: {
						name: 'Flagged',
						icon: 'flag-checkered',
						href: `/jobs?subView=${JobSubView.FLAGGED}`,
						permissionKey: PermissionKey.MENU_JOBS,
						allowedRoles: [Role.FULL],
					},
			businessCategorySummary?.['WARRANTY']
				? {
						name: 'Warranty',
						icon: 'file-shield',
						href: `/jobs?subView=${JobSubView.WARRANTY}`,
						notification: sumNotificationByCategory('WARRANTY'),
						permissionKey: PermissionKey.MENU_JOBS,
						allowedRoles: [Role.FULL],
					}
				: {
						name: 'Warranty',
						icon: 'file-shield',
						href: `/jobs?subView=${JobSubView.WARRANTY}`,
						permissionKey: PermissionKey.MENU_JOBS,
						allowedRoles: [Role.FULL],
					},
			{
				name: 'Leads',
				icon: 'clipboard-list',
				href: `/leads`,
				notification: openLeadsCount,
				permissionKey: PermissionKey.MENU_JOBS,
				allowedRoles: [Role.FULL],
			},
			{
				name: 'Quick Estimates',
				icon: 'file-pen',
				href: '/quick-estimate',
				notification: quickEstimatesCount,
				permissionKey: PermissionKey.MENU_QUICK_ESTIMATES,
				allowedRoles: [Role.FULL, Role.TECHNICIAN],
			},
			{
				name: 'Inbox',
				icon: 'bell',
				href: '/inbox',
				notification: !!unreadNotificationsCount ? unreadNotificationsCount : undefined,
				rightIcon: taskCountWithEmailDeliveryFailed ? 'exclamation-triangle' : '',
				permissionKey: PermissionKey.MENU_INBOX,
				allowedRoles: [Role.FULL],
			},
			{
				allowedRoles: [Role.FULL],
				href: '/date-tracker',
				icon: 'calendar-check',
				isNew: true,
				name: 'Date Tracker',
				permissionKey: PermissionKey.MENU_INBOX,
			},
			...(enable2WayEmail
				? [
						{
							name: 'Email',
							icon: 'envelope',
							isNew: true,
							href: '/email',
							permissionKey: PermissionKey.MENU_INBOX,
							allowedRoles: [Role.FULL],
						},
					]
				: []),
			{ name: 'Customers', icon: 'address-book', href: '/customers', permissionKey: PermissionKey.MENU_CUSTOMERS, allowedRoles: [Role.FULL] },
			{
				name: 'Time Tracking',
				icon: 'clock-three',
				href: '/time-tracking',
				permissionKey: PermissionKey.MENU_TIME_TRACKING,
				allowedRoles: [Role.FULL],
			},
			{
				name: 'Scheduling',
				icon: 'calendar-day',
				href: '/schedule',
				permissionKey: PermissionKey.MENU_SCHEDULING,
				allowedRoles: [Role.FULL, Role.TECHNICIAN],
			},
			{
				name: 'Labor Planning',
				icon: 'chart-gantt',
				href: '/labor-planning',
				permissionKey: PermissionKey.MENU_LABOR_PLANNING,
				allowedRoles: [Role.FULL],
			},
			{
				name: 'Purchase Orders',
				icon: 'cart-shopping',
				href: '/purchase-orders',
				permissionKey: PermissionKey.MENU_PURCHASE_ORDERS,
				allowedRoles: [Role.FULL],
			},
			{
				name: 'Work Orders',
				icon: 'clipboard-list-check',
				href: '/work-orders',
				permissionKey: PermissionKey.MENU_WORK_ORDERS,
				allowedRoles: [Role.FULL],
			},
			{
				name: 'Suppliers & Materials',
				icon: 'screwdriver-wrench',
				href: '/materials',
				permissionKey: PermissionKey.MENU_SUPPLIERS_AND_MATERIALS,
				allowedRoles: [Role.FULL],
			},
			{
				name: 'Accounting',
				// Led by its first visible page rather than fixed to Billing and MENU_TRANSACTIONS, so a user
				// who can only see Payroll Timesheets still gets the group, opening on that page.
				href: accountingSubItems[0]?.href ?? '/billing',
				icon: 'money-bill-transfer',
				permissionKey: accountingSubItems[0]?.permissionKey ?? PermissionKey.MENU_TRANSACTIONS,
				allowedRoles: [Role.FULL],
				subItems: accountingSubItems,
			},
			{
				name: 'Finance Center',
				href: '/finance-center',
				icon: 'building-columns',
				// TODO: wire up an alert count (notification) once an implementation exists.
				notification: undefined,
				permissionKey: PermissionKey.MENU_FINANCE_CENTER,
				allowedRoles: [Role.FULL],
			},
			{
				name: 'Reporting',
				icon: 'chart-line-up',
				href: '/reporting',
				subItems: [
					{
						name: 'Jobs',
						icon: 'chart-user',
						href: '/reporting/jobs',
						permissionKey: PermissionKey.MENU_REPORTING_JOBS,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Estimates',
						icon: 'chart-user',
						href: '/reporting/estimates',
						permissionKey: PermissionKey.MENU_REPORTING_ESTIMATES,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Commercial Bid Report',
						icon: 'chart-user',
						href: '/reporting/commercialBids',
						permissionKey: PermissionKey.MENU_REPORTING_ESTIMATES,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Inventory',
						icon: 'shelves',
						href: '/reporting/inventory',
						otherPaths: ['/reporting/estimateInventory', '/reporting/purchaseOrderInventory'],
						permissionKey: PermissionKey.MENU_REPORTING_ESTIMATES,
						allowedRoles: [Role.FULL],
					},

					{
						name: 'Purchase Orders',
						icon: 'chart-user',
						href: '/reporting/purchaseOrders',
						otherPaths: ['/reporting/purchaseOrderLineItems'],
						permissionKey: PermissionKey.MENU_REPORTING_PURCHASE_ORDERS,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Customers',
						icon: 'chart-user',
						href: '/reporting/customers',
						permissionKey: PermissionKey.MENU_REPORTING_JOBS,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'AR Transactions',
						icon: 'chart-user',
						href: '/reporting/transactions',
						permissionKey: PermissionKey.MENU_REPORTING_AR_TRANSACTIONS,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Jobs by Employee',
						icon: 'shelves',
						href: '/reporting/jobsByEmployee',
						permissionKey: PermissionKey.MENU_REPORTING_JOBS,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Cogs',
						icon: 'cart-flatbed',
						href: '/reporting/cogs',
						permissionKey: PermissionKey.MENU_REPORTING_JOBS,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Cost Code Sales',
						icon: 'tags',
						href: '/reporting/salesByCostCode',
						permissionKey: PermissionKey.MENU_REPORTING_JOBS,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Scheduled Jobs',
						icon: 'calendar-day',
						href: '/reporting/scheduledJobs',
						permissionKey: PermissionKey.MENU_REPORTING_JOBS,
						allowedRoles: [Role.FULL],
					},
					{
						name: 'Sync Log',
						icon: 'right-left-large',
						href: '/reporting/sync-log',
						permissionKey: PermissionKey.MENU_REPORTING_JOBS,
						allowedRoles: [Role.FULL],
					},
					{
						allowedRoles: [Role.FULL],
						href: '/reporting/submittals',
						icon: 'paper-plane',
						name: 'Submittals',
						permissionKey: PermissionKey.MENU_REPORTING_JOBS,
					},
				].filter((item) => hasPermission(userPermissions, item.permissionKey) && isRoleAllowed(role, item.allowedRoles)), // Filter subItems
				permissionKey: PermissionKey.MENU_REPORTING_JOBS,
				allowedRoles: [Role.FULL],
			},
			// Deliberately NOT gated on `isLeadsEnabled`, like Leads itself and the two Reach pages below:
			// the page upsells rather than disappearing. See docs/leads-redesign.md, "Page gate".
			{
				allowedRoles: [Role.FULL],
				href: `/leads/conversion`,
				icon: 'filter',
				name: 'Leads Conversion',
				permissionKey: PermissionKey.MENU_JOBS,
			},
			{
				allowedRoles: [Role.FULL, Role.TECHNICIAN],
				href: '/document-hub',
				icon: 'folder-image',
				name: 'Document Hub',
				permissionKey: PermissionKey.MENU_DOCUMENT_HUB,
			},
			...(enableRevenueAgent || isPlaybooksFeatureEnabled
				? [
						{
							allowedRoles: [Role.FULL],
							href: '/agents',
							icon: 'wand-magic-sparkles',
							name: 'AI Agents',
							permissionKey: PermissionKey.MENU_TRANSACTIONS,
						},
					]
				: []),
			// Deliberately NOT gated on `enableReachMarketing` / `enableReachAiVoice`, unlike the
			// subscription features above — both pages upsell instead. See docs/reach.md.
			// The "- Demo" suffix marks a sales demo environment, so nobody mistakes it for live data.
			{
				allowedRoles: [Role.FULL],
				href: '/marketing',
				icon: 'rectangle-ad',
				name: `Marketing${enableReachMarketingDemo ? ' - Demo' : ''}`,
				permissionKey: PermissionKey.MENU_MARKETING,
			},
			{
				allowedRoles: [Role.FULL],
				href: '/ai-voice',
				icon: 'phone-volume',
				name: `AI Voice${enableReachAiVoiceDemo ? ' - Demo' : ''}`,
				permissionKey: PermissionKey.MENU_AI_VOICE,
			},
		],
		bottomNav: [{ name: 'Settings', icon: 'gears', href: '/settings', permissionKey: PermissionKey.MENU_SETTINGS, allowedRoles: [Role.FULL] }],
	};

	if (isSuperAdmin) {
		navItems.bottomNav.push({
			allowedRoles: [Role.FULL],
			href: '/internal',
			icon: 'toolbox',
			name: 'Internal Tools',
			permissionKey: PermissionKey.MENU_SETTINGS,
		});
	}

	// Filter navigation and bottomNav based on both user permissions and role
	return {
		navigation: navItems.navigation.filter(
			(item) => hasPermission(userPermissions, item.permissionKey) && isRoleAllowed(role, item.allowedRoles)
		),
		bottomNav: navItems.bottomNav.filter((item) => hasPermission(userPermissions, item.permissionKey) && isRoleAllowed(role, item.allowedRoles)),
	};
};
export const useJobNavItemsForRole = (jobRoot: string) => {
	const { role, allowedPermissions, enablePlanAnalysis } = useUserContext();
	const { jobEstimates, hiddenJobNavItemIds, isAIAProgressBilling } = useJobContext();

	// If there's no role on this user something went sideways somewhere.
	if (!role) {
		return [];
	}

	const navItems = [
		{
			id: 'job-nav-overview',
			name: 'Overview',
			href: jobRoot,
			icon: 'gauge-circle-bolt',
			permissionKey: PermissionKey.JOB_MENU_OVERVIEW,
			allowedRoles: [Role.FULL, Role.TECHNICIAN],
		},
		{
			id: 'job-nav-attachments',
			name: 'Attachments',
			href: `${jobRoot}/attachments?view=Grid`,
			icon: 'file-image',
			permissionKey: PermissionKey.JOB_MENU_ATTACHMENTS,
			allowedRoles: [Role.FULL, Role.TECHNICIAN],
		},
		{
			id: 'job-nav-proposal-builder',
			name: 'Proposal Builder',
			href: `${jobRoot}/proposal-builder`,
			icon: 'sparkles',
			isNew: true,
			permissionKey: PermissionKey.JOB_MENU_ESTIMATES,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-bid-studio',
			name: 'Bid Studio',
			href: `${jobRoot}/bid-studio`,
			icon: 'file-magnifying-glass',
			isNew: true,
			permissionKey: PermissionKey.JOB_MENU_ESTIMATES,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-estimates',
			name: 'Estimates',
			href: jobEstimates.length === 1 ? `${jobRoot}/estimates/latest` : `${jobRoot}/estimates`,
			compareHref: `${jobRoot}/estimates`,
			icon: 'file-signature',
			permissionKey: estimatesNavAccess.permissionKey,
			allowedRoles: estimatesNavAccess.allowedRoles,
		},
		{
			id: 'job-nav-change-orders',
			name: 'Change Orders',
			href: `${jobRoot}/change-orders`,
			icon: 'exchange',
			permissionKey: PermissionKey.JOB_MENU_CHANGE_ORDERS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-submittals',
			name: 'Submittals',
			href: `${jobRoot}/submittals`,
			icon: 'paper-plane',
			// TODO: New permission?
			permissionKey: PermissionKey.JOB_MENU_ATTACHMENTS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-glass-builder',
			name: 'Glass Builder',
			href: `${jobRoot}/glass-builder`,
			icon: 'tools',
			permissionKey: PermissionKey.JOB_MENU_GLASS_BUILDER,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-work-orders',
			name: 'Work Orders',
			href: `${jobRoot}/work-orders`,
			icon: 'gauge-circle-bolt',
			permissionKey: PermissionKey.JOB_MENU_WORK_ORDERS,
			allowedRoles: [Role.FULL, Role.TECHNICIAN],
		},
		{
			id: 'job-nav-floor-plans',
			name: 'Floor Plan',
			href: `${jobRoot}/floor-plan`,
			icon: 'house-building',
			permissionKey: PermissionKey.JOB_MENU_FLOORPLANS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-measures-and-installs',
			name: 'Measures & Installs',
			href: `${jobRoot}/site-photos`,
			icon: 'ruler-combined',
			permissionKey: PermissionKey.JOB_MENU_MEASURES_AND_INSTALLS,
			allowedRoles: [Role.FULL, Role.TECHNICIAN],
		},
		{
			id: 'job-nav-daily-reports',
			name: 'Daily Reports',
			href: `${jobRoot}/daily-reports`,
			icon: 'file-chart-column',
			permissionKey: PermissionKey.JOB_MENU_DAILY_REPORTS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-quote-requests',
			name: 'Quote Requests',
			href: `${jobRoot}/quote-requests`,
			icon: 'comments',
			permissionKey: PermissionKey.JOB_MENU_QUOTE_REQUESTS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-purchase-orders',
			name: 'Purchase Orders',
			href: `${jobRoot}/purchase-orders`,
			icon: 'shopping-cart',
			permissionKey: PermissionKey.JOB_MENU_PURCHASE_ORDERS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-phases',
			name: 'Phases',
			href: `${jobRoot}/phases`,
			icon: 'tasks',
			permissionKey: PermissionKey.JOB_MENU_PHASES,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-key-dates',
			name: 'Job Tasks',
			href: `${jobRoot}/key-dates/edit`,
			icon: 'gauge-circle-bolt',
			permissionKey: PermissionKey.JOB_MENU_KEY_DATES,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-planning',
			name: 'Gantt Chart',
			href: `${jobRoot}/planning`,
			icon: 'chart-gantt',
			permissionKey: PermissionKey.JOB_MENU_FLOORPLANS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-materials-equipment',
			name: 'Materials and Equipment',
			href: `${jobRoot}/materials-equipment`,
			icon: 'forklift',
			permissionKey: PermissionKey.MENU_PURCHASE_ORDERS,
			allowedRoles: [Role.FULL, Role.TECHNICIAN],
		},
		{
			id: 'job-nav-payments',
			name: 'Payments',
			href: `${jobRoot}/payments`,
			icon: 'sack-dollar',
			permissionKey: PermissionKey.JOB_MENU_PAYMENTS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-job-costing',
			name: 'Job Costing',
			href: `${jobRoot}/job-costing`,
			icon: 'chart-mixed',
			permissionKey: PermissionKey.JOB_MENU_JOB_COSTING,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-job-sync-log',
			name: 'Accounting Sync',
			href: `${jobRoot}/sync-log`,
			icon: 'right-left-large',
			permissionKey: PermissionKey.JOB_MENU_ESTIMATES,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-smart-takeoff',
			name: 'Smart Takeoff',
			href: `${jobRoot}/smart-takeoff`,
			icon: 'brain-circuit',
			isNew: true,
			permissionKey: PermissionKey.JOB_MENU_FLOORPLANS,
			allowedRoles: [Role.FULL],
		},
		{
			id: 'job-nav-commercial-billing',
			name: 'Progress Billing',
			href: `${jobRoot}/commercial-billing`,
			icon: 'briefcase',
			permissionKey: PermissionKey.JOB_MENU_COMMERCIAL_BILLING,
			allowedRoles: [Role.FULL],
		},
	] satisfies readonly JobNavItem[];

	// Fails to compile if the ids built above drift from the canonical list in ~/layout/jobNavItems.
	assertJobNavIdsAreExhaustive<(typeof navItems)[number]['id']>(true);

	// Filter navItems based on permissions, role, the job category's hidden-item list, whether the job
	// has isAIAProgressBilling set for Progress Billing, and the business's plan analysis flag for Bid Studio.
	return navItems.filter(
		(item) =>
			hasPermission(allowedPermissions, item.permissionKey) &&
			isRoleAllowed(role, item.allowedRoles) &&
			!hiddenJobNavItemIds.includes(item.id) &&
			(item.id !== 'job-nav-commercial-billing' || isAIAProgressBilling) &&
			(item.id !== 'job-nav-bid-studio' || enablePlanAnalysis)
	);
};
